import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import TicketEmAtendimentoModal from "../TicketEmAtendimentoModal";
import { abrirConversaComContato } from "../../utils/abrirConversa";

// Cartão de contato recebido dentro de uma mensagem (vCard).
//
// Este componente já criou contato no banco só para se desenhar: a cada
// renderização ele fazia `POST /contacts`, e quando a criação falhava (número
// duplicado, sem permissão de escrita, sem conexão) aparecia um toast de erro
// mesmo tendo dado tudo certo no envio, e o botão "Conversar" ficava travado
// porque `selectedContact.number` continuava vazio. Agora a montagem só
// consulta (`GET /contacts/lookup`); criar é ação explícita da pessoa.
const VcardPreview = ({ contact, numbers }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const numeroDoCartao = String(numbers || "").replace(/\D/g, "");

  // Contato já em atendimento com outra pessoa: a janela mostra com quem
  // está e oferece pedir a transferência.
  const [emAtendimento, setEmAtendimento] = useState(null);
  // Pode existir no banco apenas como identidade técnica de uma mensagem,
  // sem ainda fazer parte da agenda.
  const [contatoEncontrado, setContatoEncontrado] = useState(null);
  const [numeroConsultado, setNumeroConsultado] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const consultando =
    Boolean(numeroDoCartao) && numeroConsultado !== numeroDoCartao;

  useEffect(() => {
    if (!numeroDoCartao) return undefined;

    let cancelado = false;

    const debounce = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/lookup", {
          params: { number: numeroDoCartao },
        });
        if (!cancelado) setContatoEncontrado(data || null);
      } catch {
        // Consulta silenciosa: não vale interromper a leitura da
        // conversa com um toast. O cartão continua utilizável.
        if (!cancelado) setContatoEncontrado(null);
      } finally {
        if (!cancelado) setNumeroConsultado(numeroDoCartao);
      }
    }, 300);

    return () => {
      cancelado = true;
      clearTimeout(debounce);
    };
  }, [numeroDoCartao]);

  // Cria o contato na agenda e devolve o registro. Usada tanto pelo botão
  // "Adicionar contato" quanto pelo "Conversar" de um contato ainda não salvo.
  const salvarNaAgenda = async () => {
    const payload = { name: contact || numeroDoCartao };
    let data;
    if (contatoEncontrado?.id) {
      ({ data } = await api.put(
        `/contacts/${contatoEncontrado.id}/agenda`,
        payload,
      ));
    } else {
      // O vCard veio do próprio WhatsApp, portanto o número já foi validado.
      // Reconsultar pela conexão padrão criava um falso erro de conexão.
      const criado = await api.post("/contact", {
        ...payload,
        number: numeroDoCartao,
      });
      ({ data } = await api.put(`/contacts/${criado.data.id}/agenda`, payload));
    }
    setContatoEncontrado(data);
    return data;
  };

  const adicionarContato = async () => {
    setOcupado(true);
    try {
      await salvarNaAgenda();
    } catch (err) {
      toastError(err);
    } finally {
      setOcupado(false);
    }
  };

  const abrirConversa = async () => {
    setOcupado(true);
    try {
      const alvo = contatoEncontrado || (await salvarNaAgenda());

      const { ticket, emAtendimento: comOutro } = await abrirConversaComContato(
        {
          contactId: alvo.id,
          userId: user.id,
        },
      );

      if (comOutro) {
        setEmAtendimento(comOutro);
        return;
      }

      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setOcupado(false);
    }
  };

  const estaNaAgenda =
    contatoEncontrado?.savedInAgenda !== false && Boolean(contatoEncontrado);
  const nomeExibido = contatoEncontrado?.name || contact || numeroDoCartao;
  const numeroExibido = contatoEncontrado?.number || numeroDoCartao;

  const iniciais = (nomeExibido || "?")
    .split(" ")
    .slice(0, 2)
    .map((parte) => parte[0])
    .join("")
    .toUpperCase();

  return (
    <div className="w-[272px] overflow-hidden rounded-lg border bg-card">
      <TicketEmAtendimentoModal
        aberto={Boolean(emAtendimento)}
        atendimento={emAtendimento}
        aoFechar={() => setEmAtendimento(null)}
      />

      <div className="flex items-center gap-2.5 p-3">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage
            src={contatoEncontrado?.profilePicUrl}
            alt={nomeExibido}
          />
          <AvatarFallback>{iniciais}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{nomeExibido}</p>
          {numeroExibido && (
            <p className="truncate text-xs tabular-nums text-muted-foreground">
              {numeroExibido}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            {consultando
              ? "Verificando contatos…"
              : estaNaAgenda
                ? "Já está nos contatos"
                : "Ainda não está nos contatos"}
          </p>
        </div>
      </div>

      {/* Duas ações lado a lado, divididas por um traço, como no cartão de
			    contato do próprio WhatsApp. "Adicionar contato" só aparece enquanto
			    a pessoa não está na agenda — depois de salva, sobra "Conversar"
			    ocupando a linha inteira. */}
      <div
        className={cn(
          "grid border-t",
          estaNaAgenda || consultando ? "grid-cols-1" : "grid-cols-2 divide-x",
        )}
      >
        <Button
          variant="ghost"
          className="w-full rounded-none px-2 text-xs font-medium text-primary hover:text-primary"
          onClick={abrirConversa}
          disabled={!numeroDoCartao || ocupado || consultando}
        >
          Conversar
        </Button>
        {!estaNaAgenda && !consultando && (
          <Button
            variant="ghost"
            className="w-full rounded-none px-2 text-xs font-medium text-primary hover:text-primary"
            onClick={adicionarContato}
            disabled={!numeroDoCartao || ocupado}
          >
            Adicionar contato
          </Button>
        )}
      </div>
    </div>
  );
};

export default VcardPreview;
